import React from "react";
import {
  Document,
  Page,
  View,
  Text,
  Image,
  StyleSheet,
  Svg,
  Path,
  Circle,
  Line,
} from "@react-pdf/renderer";
import { Booking } from "../../interfaces/booking.interface";
import { BookingStatus } from "../../enums/booking.enum";

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 10,
    fontFamily: "Helvetica",
    color: "#111827",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 6,
  },
  logo: { width: 150, height: 56, objectFit: "contain", objectPositionX: 0 },
  pageTitle: { fontSize: 18, fontFamily: "Helvetica-Bold" },
  divider: { borderBottomWidth: 2, borderBottomColor: "#000", marginVertical: 6 },
  thinDivider: { borderBottomWidth: 0.5, borderBottomColor: "#d1d5db", marginVertical: 3 },
  section: { marginBottom: 10 },
  sectionTitle: { fontSize: 12, fontFamily: "Helvetica-Bold", marginBottom: 3 },
  row: { flexDirection: "row", marginBottom: 2 },
  label: { width: "28%", fontFamily: "Helvetica-Bold" },
  colon: { width: 10 },
  value: { flex: 1 },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#f3f4f6",
    paddingVertical: 4,
    paddingHorizontal: 2,
    fontFamily: "Helvetica-Bold",
    fontSize: 8,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 0.5,
    borderBottomColor: "#e5e7eb",
    paddingVertical: 3,
    paddingHorizontal: 2,
    fontSize: 8,
  },
  c5: { width: "5%", textAlign: "center" },
  c12: { width: "12%", textAlign: "center" },
  c13: { width: "13%", textAlign: "center" },
  c9: { width: "9%", textAlign: "center" },
  c16: { width: "16%", textAlign: "center" },
  fareGroup: { justifyContent: "center" },
  fareRow: { flexDirection: "row", alignItems: "center", marginBottom: 4 },
  fareLabel: { fontSize: 9 },
  bullet: { marginBottom: 3, lineHeight: 1.4 },
  small: { fontSize: 8, color: "#6b7280" },
  bold: { fontFamily: "Helvetica-Bold" },
  fareRow2: { flexDirection: "row", flexWrap: "wrap", justifyContent: "space-around" },
});

const fmtDate = (s: string) =>
  s
    ? new Date(s).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : "-";

const fmtDeparture = (s: string) =>
  s
    ? new Date(s).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      })
    : "";

const fmtTime = (s: string) =>
  s ? new Date(s).toLocaleTimeString("en-GB", { timeStyle: "short" }) : "";

const cap = (s: string) =>
  s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "";

interface Props {
  booking: Booking;
  logoDataUrl: string;
  providerImageUrl?: string;
  user?: { fullName?: string; email?: string; phone?: string };
  restrictedProviderImages?: Record<string, string | null>;
}

const CheckCircle = () => (
  <Svg viewBox="0 0 24 24" style={{ width: 11, height: 11, marginRight: 4 }}>
    <Circle cx="12" cy="12" r="9.75" fill="#16a34a" />
    <Path
      d="M7.5 12.5 L10.5 15.5 L16.5 8.5"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </Svg>
);

const XCircle = () => (
  <Svg viewBox="0 0 24 24" style={{ width: 11, height: 11, marginRight: 4 }}>
    <Circle cx="12" cy="12" r="9.75" fill="#ef4444" />
    <Line x1="8.5" y1="8.5" x2="15.5" y2="15.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
    <Line x1="15.5" y1="8.5" x2="8.5" y2="15.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
  </Svg>
);

const FareItem = ({
  included,
  label,
}: {
  included: boolean;
  label: string;
}) => (
  <View style={styles.fareRow}>
    {included ? <CheckCircle /> : <XCircle />}
    <Text style={styles.fareLabel}>{label}</Text>
  </View>
);

const ArrivalCard = ({ country }: { country: string }) => {
  if (country === "malaysia") {
    return (
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>
          MALAYSIA DIGITAL ARRIVAL CARD (MDAC)
        </Text>
        <View style={styles.divider} />
        <Text style={styles.bullet}>
          * All foreign citizens travelling to Malaysia are required to submit
          MDAC within 3 days before arrival.
        </Text>
        <Text style={styles.bullet}>
          * Visit: https://imigresen-online.imi.gov.my/mdac/main to submit.
        </Text>
      </View>
    );
  }
  if (country === "indonesia") {
    return (
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>
          INDONESIA ARRIVAL CARD SERVICE (IACS)
        </Text>
        <View style={styles.divider} />
        <Text style={styles.bullet}>
          * All foreign citizens travelling to Indonesia are required to submit
          IACS within 3 days before arrival.
        </Text>
        <Text style={styles.bullet}>
          * Visit: https://allindonesia.imigrasi.go.id to submit.
        </Text>
      </View>
    );
  }
  return null;
};

export const ItineraryPdf: React.FC<Props> = ({ booking, logoDataUrl, providerImageUrl, user, restrictedProviderImages }) => {
  const hasOutboundTax = booking.taxAmount != null;
  const hasOutboundSurcharge = booking.surchargeAmount != null;
  const hasReturnTax = booking.returnTaxAmount != null;
  const hasReturnSurcharge = booking.returnSurchargeAmount != null;
  const dest = booking.schedule.destination.country.toLowerCase();
  const origin = booking.schedule.origin.country.toLowerCase();

  const hiddenStatuses = [BookingStatus.CANCELLED, BookingStatus.REJECTED, BookingStatus.EXPIRED];
  const showReturnInfo = booking.isTwoWay && !hiddenStatuses.includes(booking.status as BookingStatus);

  const openReturnValidUntil = (() => {
    if (!booking.createdAt || booking.status === BookingStatus.WAITING_FOR_PAYMENT) {
      return "90 days from purchase";
    }
    const d = new Date(booking.createdAt);
    d.setDate(d.getDate() + 90);
    return d.toLocaleDateString("en-GB", {
      weekday: "long",
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  })();


  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <Image src={logoDataUrl} style={styles.logo} />
          <Text style={styles.pageTitle}>Booking Details</Text>
        </View>
        <View style={styles.divider} />

        {/* Booking ID */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Booking ID</Text>
          <Text>
            {booking.bookingId && booking.bookingId.length > 0
              ? booking.bookingId
              : booking._id}
          </Text>
        </View>

        {/* Booked By */}
        {user && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Booked By</Text>
            <View style={styles.divider} />
            {[
              ["Name", user.fullName],
              ["Email", user.email],
              ["Phone Number", user.phone],
            ].map(([lbl, val]) => (
              <View style={styles.row} key={lbl}>
                <Text style={styles.label}>{lbl}</Text>
                <Text style={styles.colon}>:</Text>
                <Text style={styles.value}>{val ?? "-"}</Text>
              </View>
            ))}
          </View>
        )}

        {/* Departure Information */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Departure Information</Text>
          <View style={styles.divider} />
          <View style={styles.row}>
            <Text style={styles.label}>Provider</Text>
            <Text style={styles.colon}>:</Text>
            <View style={[styles.value, { flexDirection: "row", alignItems: "center" }]}>
              {providerImageUrl && (
                <Image src={providerImageUrl} style={{ width: 16, height: 16, borderRadius: 8, marginRight: 4 }} />
              )}
              <Text>{booking.schedule.provider.name}</Text>
            </View>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Route</Text>
            <Text style={styles.colon}>:</Text>
            <Text style={styles.value}>
              {booking.schedule.origin.country},{" "}
              {booking.schedule.origin.port} -{" "}
              {booking.schedule.destination.country},{" "}
              {booking.schedule.destination.port}
            </Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Date</Text>
            <Text style={styles.colon}>:</Text>
            <Text style={styles.value}>
              {fmtDeparture(booking.schedule.departure)}
            </Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Time</Text>
            <Text style={styles.colon}>:</Text>
            <Text style={styles.value}>
              {fmtTime(booking.schedule.departure)}
            </Text>
          </View>
        </View>

        {/* Return Information */}
        {showReturnInfo && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Return Information</Text>
            <View style={styles.divider} />
            <View style={styles.row}>
              <Text style={styles.label}>Type</Text>
              <Text style={styles.colon}>:</Text>
              <Text style={styles.value}>Open Return Ticket</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Route</Text>
              <Text style={styles.colon}>:</Text>
              <Text style={styles.value}>
                {booking.schedule.destination.country},{" "}
                {booking.schedule.destination.port} -{" "}
                {booking.schedule.origin.country},{" "}
                {booking.schedule.origin.port}
              </Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Valid until</Text>
              <Text style={styles.colon}>:</Text>
              <Text style={styles.value}>{openReturnValidUntil}</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Valid with</Text>
              <Text style={styles.colon}>:</Text>
              {booking.isRestrictedReturn && booking.restrictedReturnProviders?.length ? (
                <View style={{ flex: 1 }}>
                  {booking.restrictedReturnProviders.map((p) => {
                    const img = restrictedProviderImages?.[p._id] ?? null;
                    return (
                      <View key={p._id} style={{ flexDirection: "row", alignItems: "center", marginBottom: 2 }}>
                        {img !== null ? (
                          <Image src={img} style={{ width: 12, height: 12, marginRight: 4 }} />
                        ) : (
                          <View style={{ width: 12, height: 12, marginRight: 4 }} />
                        )}
                        <Text>{p.name ?? p._id}</Text>
                      </View>
                    );
                  })}
                </View>
              ) : (
                <Text style={styles.value}>Any provider</Text>
              )}
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Note</Text>
              <Text style={styles.colon}>:</Text>
              <Text style={styles.value}>
                Subject to seat availability at counter
              </Text>
            </View>
          </View>
        )}

        {/* Passenger Details */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Passenger Details</Text>
          <View style={styles.divider} />
          <View style={styles.tableHeader}>
            <Text style={styles.c5}>No</Text>
            <Text style={styles.c12}>Passport</Text>
            <Text style={styles.c13}>Name</Text>
            <Text style={styles.c9}>Gender{"\n"}(M/F)</Text>
            <Text style={styles.c13}>Nationality{"\n"}(Country)</Text>
            <Text style={styles.c16}>Date of Birth{"\n"}(DD/MM/YY)</Text>
            <Text style={styles.c16}>Issuing Date{"\n"}(DD/MM/YY)</Text>
            <Text style={styles.c16}>Expired Date{"\n"}(DD/MM/YY)</Text>
          </View>
          {booking.tickets.map((ticket, idx) => (
            <View key={idx} style={styles.tableRow}>
              <Text style={styles.c5}>{idx + 1}</Text>
              <Text style={styles.c12}>{ticket.passportNumber}</Text>
              <Text style={styles.c13}>{ticket.fullName.toUpperCase()}</Text>
              <Text style={styles.c9}>
                {ticket.gender.charAt(0).toUpperCase()}
              </Text>
              <Text style={styles.c13}>{ticket.nationality.toUpperCase()}</Text>
              <Text style={styles.c16}>{fmtDate(ticket.dateOfBirth)}</Text>
              <Text style={styles.c16}>{fmtDate(ticket.issuingDate)}</Text>
              <Text style={styles.c16}>{fmtDate(ticket.expiredDate)}</Text>
            </View>
          ))}
        </View>

        {/* Ferry Fare Details */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>
            Ferry Ticket Fare & Service Fees Details
          </Text>
          <View style={styles.divider} />
          <View style={[styles.fareRow2, { marginVertical: 6 }]}>
            <View style={styles.fareGroup}>
              <FareItem included={true} label="Ferry Ticket Paid" />
            </View>
            <View style={styles.fareGroup}>
              <FareItem
                included={hasOutboundTax}
                label={`${cap(origin)} Sea Port Tax`}
              />
              <FareItem
                included={hasOutboundSurcharge}
                label={`${cap(origin)} Surcharge`}
              />
            </View>
            {booking.isTwoWay && (
              <View style={styles.fareGroup}>
                <FareItem
                  included={hasReturnTax}
                  label={`${cap(dest)} Return Sea Port Tax`}
                />
                <FareItem
                  included={hasReturnSurcharge}
                  label={`${cap(dest)} Return Surcharge`}
                />
              </View>
            )}
          </View>
          {[
            "1. You are required to collect your boarding pass 90 mins before your departure time or seats will be automatically released.",
            "2. This fare type is NOT transferable to another passenger.",
            "3. This fare type is capacity controlled and limited and hence, may not be available on all ferries.",
            "4. This fare type is always subject to our General Terms and Conditions.",
          ].map((t) => (
            <Text key={t} style={styles.bullet}>
              {t}
            </Text>
          ))}
        </View>

        {/* Travel Guide */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Travel Guide</Text>
          <View style={styles.divider} />
          {[
            "* Use our Self Check-In facilities, log onto our website on the facilities available.",
            "* Check-in deadlines may vary at different seaports and for particular ferries, comply with these deadlines.",
            "* It is your responsibility to ensure that you do not carry any liquids, aerosols and gels more than 100ml in your cabin baggage.",
            "* Some seaports have long queues due to complex security checks. We suggest that you check-in early and proceed immediately to the departure gate.",
            "* Guests may not use the unused checked baggage weight of other guests unless travelling on the same Itinerary.",
            "* Guests are strongly advised not to check in goods which are valuable and fragile as baggage.",
            "* You are responsible for obtaining all valid entry and exit visas, health and other required documents, ensuring that your passport meets the destination's entry requirements.",
          ].map((t) => (
            <Text key={t} style={styles.bullet}>
              {t}
            </Text>
          ))}
        </View>

        {/* Arrival Cards */}
        <ArrivalCard country={dest} />
        {showReturnInfo && dest !== origin && (
          <ArrivalCard country={origin} />
        )}
      </Page>
    </Document>
  );
};
